import { useMutation } from "@apollo/client";
import { compact } from "lodash";
import { useCallback, useMemo, useState } from "react";
import {
  CalibrationNodeFragment,
  UpdateAssessmentAnswerMutation,
  UpdateAssessmentAnswerMutationVariables,
} from "types/graphql-schema";

import getAllPrecalibrationsQuery from "@apps/reporting/graphql/get-all-precalibrations-query";
import getAssessmentAnswerCalibrationsQuery from "@apps/reporting/graphql/get-assessment-answer-calibrations-query";
import updateAssessmentAnswerMutation from "@apps/reporting/graphql/update-assessment-answer-mutation";
import { successNotificationVar } from "@cache/cache";
import Avatar from "@components/avatar/avatar";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import { Select } from "@components/select/select";
import TimeAgoCustom from "@components/time-ago/time-ago";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { TopicflowIconMap } from "@components/topicflow-icon/topicflow-icon-helpers";
import { isEmptyValue } from "@components/wysiwyg/helpers";
import TextareaWysiwyg from "@components/wysiwyg/textarea-wysiwyg";
import { classNames } from "@helpers/css";
import { assertNonNull } from "@helpers/helpers";

enum CalibrationFormStatus {
  CLOSED,
  SUGGESTING,
  EDITING,
}

const QuestionCalibrationSection = ({
  complianceProgramId,
  templateId,
  questionId,
  targetId,
  answerId,
  calibrations,
  canCalibrate,
  canSuggestCalibration,
  expanded,
  hideCollapseButton,
  isLoadingCalibrations,
  questionLabels,
  title,
}: {
  complianceProgramId?: number;
  templateId?: number;
  questionId?: number;
  targetId?: number;
  answerId?: number;
  calibrations: CalibrationNodeFragment[];
  canCalibrate?: boolean;
  canSuggestCalibration?: boolean;
  expanded?: boolean;
  hideCollapseButton?: boolean;
  isLoadingCalibrations?: boolean;
  questionLabels: Record<string, string>;
  title?: string;
}) => {
  const [isCollapsed, setIsCollapsed] = useState(!expanded);
  const [status, setStatus] = useState(CalibrationFormStatus.CLOSED);

  const [calibrationForm, setCalibrationForm] = useState<{
    integerAnswer: number | null;
    comment: string | null;
  }>({
    integerAnswer: null,
    comment: null,
  });

  const [updateAssessmentAnswer, { loading: isSavingCalibration }] =
    useMutation<
      UpdateAssessmentAnswerMutation,
      UpdateAssessmentAnswerMutationVariables
    >(updateAssessmentAnswerMutation);

  const handleCancelCalibration = useCallback(() => {
    setCalibrationForm({
      integerAnswer: null,
      comment: null,
    });
    setStatus(CalibrationFormStatus.CLOSED);
  }, []);

  const isValidCalibrationForm = useMemo(
    () =>
      complianceProgramId &&
      templateId &&
      questionId &&
      targetId &&
      !isEmptyValue(calibrationForm.comment) &&
      (status === CalibrationFormStatus.SUGGESTING ||
        calibrationForm.integerAnswer !== null),
    [
      calibrationForm,
      complianceProgramId,
      questionId,
      status,
      templateId,
      targetId,
    ],
  );

  const handleSubmitCalibration = useCallback(() => {
    if (isValidCalibrationForm) {
      const isPrecalibration = !answerId;
      updateAssessmentAnswer({
        variables: {
          complianceProgramId: assertNonNull(complianceProgramId),
          templateId: assertNonNull(templateId),
          questionId: assertNonNull(questionId),
          targetId: assertNonNull(targetId),
          answerId,
          integerAnswer: calibrationForm.integerAnswer,
          comment: assertNonNull(calibrationForm.comment),
          isSuggestion: status === CalibrationFormStatus.SUGGESTING,
          isPrecalibration,
        },
        refetchQueries: compact([
          getAssessmentAnswerCalibrationsQuery,
          isPrecalibration && getAllPrecalibrationsQuery,
        ]),

        onCompleted: () => {
          handleCancelCalibration();
          successNotificationVar({ title: "Calibration saved successfully" });
        },
      });
    }
  }, [
    complianceProgramId,
    templateId,
    questionId,
    targetId,
    answerId,
    calibrationForm,
    handleCancelCalibration,
    isValidCalibrationForm,
    status,
    updateAssessmentAnswer,
  ]);

  const questionOptions = useMemo(() => {
    return Object.keys(questionLabels).map((value) => ({
      value: parseInt(value),
      label: questionLabels[value],
    }));
  }, [questionLabels]);

  return (
    <div>
      <div className="flex items-center justify-between">
        <div className="tf-header-sm-dark">{title ?? "Calibration"}</div>
        <div className="flex items-center gap-2">
          {canCalibrate && (
            <Button
              icon={TopicflowIconMap.edit}
              disabled={status !== CalibrationFormStatus.CLOSED}
              theme={buttonTheme.iconGray}
              onClick={() => {
                setStatus(CalibrationFormStatus.EDITING);
                setIsCollapsed(false);
              }}
              aria-label="Calibrate answer button"
              tooltip="Set a rating"
            />
          )}
          {canSuggestCalibration && (
            <Button
              icon={TopicflowIconMap.chatAdd}
              disabled={status !== CalibrationFormStatus.CLOSED}
              theme={buttonTheme.iconGray}
              onClick={() => {
                setStatus(CalibrationFormStatus.SUGGESTING);
                setIsCollapsed(false);
              }}
              tooltip="Suggest a rating"
            />
          )}
          {!hideCollapseButton && (
            <Button
              icon={
                isCollapsed
                  ? TopicflowIconMap.chevronDown
                  : TopicflowIconMap.chevronUp
              }
              disabled={status !== CalibrationFormStatus.CLOSED}
              theme={buttonTheme.iconGray}
              onClick={() => setIsCollapsed(!isCollapsed)}
              aria-label="Question calibration toggle"
            />
          )}
        </div>
      </div>

      {!isCollapsed && (
        <div className="mt-4 flex flex-col gap-4 relative">
          {calibrations.length === 0 &&
            !isLoadingCalibrations &&
            status === CalibrationFormStatus.CLOSED && (
              <div className="p-2 border rounded-lg text-center bg-gray-50 border-gray-100 text-gray-500 text-sm">
                No calibrations yet
              </div>
            )}
          {calibrations.length === 0 && isLoadingCalibrations && (
            <div className="flex flex-col gap-1">
              <div className="text-gray-500 bg-gray-100 animate-pulse rounded-full h-5 w-92" />
              <div className="text-gray-500 bg-gray-100 animate-pulse rounded-md h-4 w-48" />
            </div>
          )}

          {(calibrations.length > 0 ||
            status !== CalibrationFormStatus.CLOSED) && (
            <div className="absolute left-[11px] top-0 bottom-0 w-[2px] bg-gray-200"></div>
          )}

          {status !== CalibrationFormStatus.CLOSED && (
            <div className="pl-10 relative">
              <div
                className={classNames(
                  "absolute left-0 -top-0.5 h-6 w-6 rounded-full flex items-center justify-center",
                  status === CalibrationFormStatus.SUGGESTING && "bg-gray-200",
                  status === CalibrationFormStatus.EDITING && "bg-amber-100",
                )}
              >
                <TopicflowIcon
                  type={
                    status === CalibrationFormStatus.SUGGESTING
                      ? "chatAdd"
                      : "edit"
                  }
                  className={classNames(
                    "h-4 w-4",
                    status === CalibrationFormStatus.SUGGESTING &&
                      "text-gray-600",
                    status === CalibrationFormStatus.EDITING &&
                      "text-amber-700",
                  )}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="text-gray-700 font-medium">
                  {status === CalibrationFormStatus.SUGGESTING
                    ? "Suggest a rating"
                    : "Set a rating"}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    text="Cancel"
                    disabled={isSavingCalibration}
                    size={ButtonSize.medium}
                    onClick={handleCancelCalibration}
                  />
                  <Button
                    theme={buttonTheme.primary}
                    text="Submit"
                    disabled={isSavingCalibration || !isValidCalibrationForm}
                    size={ButtonSize.medium}
                    onClick={handleSubmitCalibration}
                  />
                </div>
              </div>

              <div className="mt-4 flex flex-col gap-2">
                <div className="text-gray-600 font-semibold text-xs">
                  {status === CalibrationFormStatus.SUGGESTING
                    ? "Suggested answer (optional)"
                    : "New answer"}
                </div>
                <Select
                  value={calibrationForm.integerAnswer}
                  options={questionOptions}
                  placeholder="Select an answer"
                  onChange={(option) =>
                    setCalibrationForm((prev) => ({
                      ...prev,
                      integerAnswer: option.value,
                    }))
                  }
                />
              </div>

              <div className="mt-4 flex flex-col gap-2">
                <div className="text-gray-600 font-semibold text-xs">
                  Comment
                </div>
                <TextareaWysiwyg
                  editable
                  value={calibrationForm.comment}
                  className=""
                  onChangeValue={(comment) => {
                    setCalibrationForm((prev) => ({
                      ...prev,
                      comment,
                    }));
                  }}
                />
              </div>
            </div>
          )}

          {calibrations.map((calibration) => {
            const isSuggestion = calibration.isSuggestion;
            return (
              <div key={calibration.id} className="pl-10 relative">
                <div
                  className={classNames(
                    "absolute left-0 -top-0.5 h-6 w-6 rounded-full flex items-center justify-center",
                    isSuggestion && "bg-gray-200",
                    !isSuggestion && "bg-amber-100",
                  )}
                >
                  <TopicflowIcon
                    type={isSuggestion ? "chatAdd" : "edit"}
                    className={classNames(
                      "h-4 w-4",
                      isSuggestion && "text-gray-600",
                      !isSuggestion && "text-amber-700",
                    )}
                  />
                </div>
                <div className="text-gray-500 flex gap-2 items-center">
                  <Avatar user={calibration.creator} size="4" />
                  <span className="font-medium text-gray-700">
                    {calibration.creator?.name || "Unknown"}
                  </span>
                  <span className="text-gray-400 text-xs font-normal">
                    (<TimeAgoCustom date={calibration.created} />)
                  </span>
                </div>

                {(calibration.__typename ===
                  "RangeAssessmentAnswerCalibrationNode" ||
                  calibration.__typename ===
                    "TalentIndicatorAssessmentAnswerCalibrationNode") &&
                  calibration.integerAnswer && (
                    <div
                      className={classNames(
                        "mt-1 flex items-center gap-1",
                        isSuggestion ? "text-gray-600" : "text-amber-700",
                      )}
                    >
                      <div className="text-xs">
                        {isSuggestion ? "Suggested rating:" : "Rating updated:"}
                      </div>

                      <div className="text-xs font-medium flex items-center gap-1">
                        {isSuggestion ? (
                          <>
                            {questionLabels[calibration.integerAnswer || 0]}
                            {questionLabels[calibration.integerAnswer || 0] !==
                              `${calibration.integerAnswer}` &&
                              ` (${calibration.integerAnswer})`}
                          </>
                        ) : calibration.isPrecalibration ? (
                          <>
                            {questionLabels[calibration.integerAnswer || 0]}
                            {questionLabels[calibration.integerAnswer || 0] !==
                              `${calibration.integerAnswer}` &&
                              ` (${calibration.integerAnswer})`}
                          </>
                        ) : (
                          <>
                            {questionLabels[calibration.prevIntegerAnswer || 0]}
                            {questionLabels[
                              calibration.prevIntegerAnswer || 0
                            ] !== `${calibration.prevIntegerAnswer}` &&
                              ` (${calibration.prevIntegerAnswer})`}
                            <TopicflowIcon
                              type="rightArrow"
                              className="h-3 w-3"
                            />
                            {questionLabels[calibration.integerAnswer || 0]}
                            {questionLabels[calibration.integerAnswer || 0] !==
                              `${calibration.integerAnswer}` &&
                              ` (${calibration.integerAnswer})`}
                          </>
                        )}
                      </div>
                    </div>
                  )}
                {calibration.comment && (
                  <TextareaWysiwyg
                    value={calibration.comment}
                    editable={false}
                    className="w-full mt-3 max-w-none text-gray-500"
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default QuestionCalibrationSection;
